import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { useTheme } from "@mui/material";
import FlexBetween from "shared/FlexBetween";
import IconButton from "@mui/material/IconButton";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ConfirmationDialog from "shared/ConfirmationDialog";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Spinner from "shared/spinner/Spinner";
import useAWSDetails from "shared/useAWSDetails";
import { getCalendarPdfUrl } from "shared/utils";

function EditProfile() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { id } = useParams();

  // Form state
  const [employeeId, setEmployeeId] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [number, setNumber] = useState("");
  const [birthDate, setBirthDate] = useState("");
  const [gender, setGender] = useState("");
  const [address, setAddress] = useState("");
  const [department, setDepartment] = useState("");
  const [workLocation, setWorkLocation] = useState("");
  const [role, setRole] = useState("");
  const [selectedGroup, setSelectedGroup] = useState("");
  const [country, setCountry] = useState("");
  const [profilePic, setProfilePic] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [originalProfilePic, setOriginalProfilePic] = useState(null);

  // UI state
  const [isLoading, setIsLoading] = useState(false);
  const [uploadConfirmationOpen, setUploadConfirmationOpen] = useState(false);
  const [loggedUserRole, setLoggedUserRole] = useState("");
  const [groups, setGroups] = useState([]);
  const [numberError, setNumberError] = useState("");
  const [errors, setErrors] = useState({});
  const { fetchedBucketName, fetchedRegion } = useAWSDetails();

    const [loggedUser, setLoggedUser] = useState([]);

  // Fetch user data
  useEffect(() => {
    if (id) {
      setIsLoading(true);
      axios
        .get(`${process.env.REACT_APP_API_URL}getUser/${id}`)
        .then((result) => {
          const userData = result.data;
          setEmployeeId(userData.employeeId || "");
          setName(userData.name || "");
          setEmail(userData.email || "");
          setNumber(userData.number || "");
          setBirthDate(userData.birthDate || "");
          setGender(userData.gender || "");
          setAddress(userData.address || "");
          setDepartment(userData.department || "");
          setWorkLocation(userData.workLocation || "");
          setRole(userData.role || "");
          setSelectedGroup(userData.groupname || "");
          setCountry(userData.country || "");
          setOriginalProfilePic(userData.profilepic || null);
        })
        .catch((err) => {
          console.error("Error fetching user data:", err);
          toast.error("Failed to load user data");
        })
        .finally(() => setIsLoading(false));
    }
  }, [id]);

  // Get logged in user's role
  useEffect(() => {
    axios
      .get(process.env.REACT_APP_API_URL + "profilepage", {
        withCredentials: true,
      })
      .then((response) => {
        setLoggedUser(response.data);
        setLoggedUserRole(response.data.role);
      })
      .catch((error) => {
        console.error("Error fetching user data:", error);
      });
  }, []);

  // Fetch groups
  useEffect(() => {
    axios
      .get(process.env.REACT_APP_API_URL + "groups/getgroups")
      .then((result) => {
        setGroups(result.data);
      })
      .catch((error) => {
        console.error("Error fetching groups:", error);
      });
  }, []);

  // Handle profile picture change
  const handleProfilePicChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setProfilePic(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Form validation
  const validateForm = () => {
    let isValid = true;
    let newErrors = {};

    // Phone number validation
    const phoneRegex = /^\d{10}$/;
    if (number && !phoneRegex.test(number)) {
      setNumberError("Please enter a valid 10-digit phone number");
      toast.error("Please enter a valid 10-digit phone number");
      isValid = false;
    } else {
      setNumberError("");
    }

    // Name validation
    if (!name.trim()) {
      newErrors.name = "Name is required";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    setUploadConfirmationOpen(false);

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    // Create form data for submission
    const formData = new FormData();
    formData.append("employeeId", employeeId);
    formData.append("name", name);
    formData.append("email", email);
    formData.append("number", number);
    formData.append("birthDate", birthDate);
    formData.append("gender", gender);
    formData.append("address", address);
    formData.append("department", department);
    formData.append("workLocation", workLocation);
    formData.append("role", role);
    formData.append("groupname", selectedGroup);
    formData.append("country", country);

    // If a new profile picture was selected, append it
    if (profilePic) {
      formData.append("file", profilePic);
    }

    axios
      .put(`${process.env.REACT_APP_API_URL}editProfile/${id}`, formData)
      .then((result) => {
        toast.success("Profile updated successfully", {
          autoClose: 3000,
          onClose: () => navigate(-1),
        });
      })
      .catch((err) => {
        if (err.response && err.response.status === 400) {
          toast.error(err.response.data.error || "Failed to update profile");
          if (err.response.data.error && err.response.data.error.includes("phone number")) {
            setNumberError(err.response.data.error);
          }
        } else {
          toast.error("An error occurred while updating profile");
          console.error(err);
        }
      })
      .finally(() => setIsLoading(false));
  };

  // Check if the user can edit a field based on their role
  const canEdit = (fieldName) => {
    if (loggedUserRole === "admin" || loggedUserRole === "hr" || loggedUserRole === "manager" || loggedUserRole === "superadmin") {
      return true;
    }

    const editableFields = ["name", "number", "address", "gender", "workLocation"];
    return editableFields.includes(fieldName);
  };

  useEffect(() => {
    // Set profile picture URL if available
    if (loggedUser.profilepic && employeeId) {
      setProfilePic(`https://${fetchedBucketName}.s3.${fetchedRegion}.amazonaws.com/${getCalendarPdfUrl()}/employees/${employeeId}/profile-picture/${loggedUser.profilepic
        }`);
    }
  }, [employeeId]);

  return (
    <div>
      <FlexBetween sx={{ position: "fixed" }}>
        <IconButton
          onClick={() => navigate(-1)}
          style={{ color: "#04497a", marginTop: "10px", marginLeft: "10px" }}
        >
          <ArrowBackIcon />
        </IconButton>
      </FlexBetween>

      {isLoading && <Spinner />}
      
      <div className="main-wrapper" style={{ color: theme.palette.background.text }}>
        <div className="form-wrapper">
          <form onSubmit={(e) => e.preventDefault()}>
            <div className="formbold-form-title">
              <h3 className="header">Edit Profile</h3>
            </div>

            <div className="grid-2-cols">
              {/* Personal Information Section */}
              <div className="form-section">
                <h4>Personal Information</h4>

                <div className="formbold-mb-3">
                  <label className="formbold-form-label">Employee ID</label>
                  <input
                    type="text"
                    className="formbold-form-input"
                    value={employeeId}
                    disabled={loggedUserRole === "employee" || loggedUserRole === "consultant"}
                  />
                </div>

                <div className="formbold-mb-3">
                  <label className="formbold-form-label">Full Name</label>
                  <input
                    type="text"
                    className="formbold-form-input"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    disabled={!canEdit("name")}
                  />
                  {errors.name && (
                    <span className="error-message">{errors.name}</span>
                  )}
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "15px" }}>
                  <div className="formbold-mb-3">
                    <label className="formbold-form-label">Date of Birth</label>
                    <input
                      type="date"
                      className="formbold-form-input"
                      value={birthDate}
                      onChange={(e) => setBirthDate(e.target.value)}
                      disabled={loggedUserRole === "employee" || loggedUserRole === "consultant"}
                    />
                  </div>

                  <div className="formbold-mb-3">
                    <label className="formbold-form-label">Gender</label>
                    <select
                      className="formbold-form-input"
                      value={gender || ""}
                      onChange={(e) => setGender(e.target.value)}
                      disabled={!canEdit("gender")}
                    >
                      <option value="">Choose...</option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                      <option value="others">Others</option>
                    </select>
                  </div>
                </div>

                <div className="formbold-mb-3">
                  <label className="formbold-form-label">Address</label>
                  <input
                    type="text"
                    className="formbold-form-input"
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    disabled={!canEdit("address")}
                  />
                </div>
              </div>

              {/* Contact & Profile Information */}
              <div className="form-section">
                <h4>Contact Information</h4>
                
                <div className="formbold-mb-3">
                  <label className="formbold-form-label">Email</label>
                  <input
                    type="email"
                    className="formbold-form-input"
                    value={email}
                    disabled
                  />
                </div>

                <div className="formbold-mb-3">
                  <label className="formbold-form-label">Phone Number</label>
                  <input
                    type="number"
                    className="formbold-form-input"
                    value={number}
                    onChange={(e) => {
                      setNumber(e.target.value);
                      setNumberError("");
                    }}
                    disabled={!canEdit("number")}
                  />
                  {numberError && (
                    <div className="error-message">{numberError}</div>
                  )}
                </div>

                <div className="formbold-mb-3">
                  <label className="formbold-form-label">Profile Picture</label>
                  <div className="upload-section">
                    {(previewImage || profilePic) && (
                      <div className="picture-preview" style={{ marginBottom: "10px" }}>
                        <img 
                          src={previewImage || profilePic} 
                          alt="Profile Preview" 
                          style={{ maxWidth: "200px", maxHeight: "200px", borderRadius: "8px" }} 
                        />
                      </div>
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleProfilePicChange}
                      className="formbold-form-input"
                      style={{ padding: "10px" }}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Professional Information Section */}
            <div className="form-section">
              <h4>Professional Information</h4>

              <div className="grid-3-cols">
                <div className="formbold-mb-3">
                  <label className="formbold-form-label">Department</label>
                  <select
                    className="formbold-form-input"
                    value={department || ""}
                    onChange={(e) => setDepartment(e.target.value)}
                    disabled={loggedUserRole === "employee" || loggedUserRole === "consultant"}
                  >
                    <option value="">Choose...</option>
                    <option value="Engineering">Engineering</option>
                    <option value="HRBP/Admin">HRBP/Admin</option>
                    <option value="Human Resource">Human Resource</option>
                    <option value="Business">Business</option>
                  </select>
                </div>

                <div className="formbold-mb-3">
                  <label className="formbold-form-label">Country</label>
                  <select
                    className="formbold-form-input"
                    value={country || ""}
                    onChange={(e) => setCountry(e.target.value)}
                    disabled={loggedUserRole === "employee" || loggedUserRole === "consultant"}
                  >
                    <option value="">Choose...</option>
                    <option value="India">India</option>
                    <option value="US">US</option>
                  </select>
                </div>

                <div className="formbold-mb-3">
                  <label className="formbold-form-label">Work Location</label>
                  <select
                    className="formbold-form-input"
                    value={workLocation || ""}
                    onChange={(e) => setWorkLocation(e.target.value)}
                    disabled={loggedUserRole === "employee" || loggedUserRole === "consultant"}
                  >
                    <option value="">Choose...</option>
                    <option value="Banglore">Bangalore</option>
                    <option value="Pune">Pune</option>
                    <option value="Kolkata">Kolkata</option>
                    <option value="Remote">Remote</option>
                  </select>
                </div>
              </div>

              <div className="formbold-mb-3">
                <label className="formbold-form-label">Role</label>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                    gap: "15px",
                    marginTop: "10px",
                  }}
                >
                  {[
                    { value: "admin", label: "Admin" },
                    { value: "hr", label: "HR" },
                    { value: "manager", label: "Manager" },
                    { value: "employee", label: "Employee" },
                    { value: "consultant", label: "Consultant" },
                  ].map((roleOption) => (
                    <label
                      key={roleOption.value}
                      className={`role-radio-label ${
                        role === roleOption.value ? "selected" : ""
                      }`}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        padding: "12px 20px",
                        border: `1px solid ${
                          role === roleOption.value ? "#04497a" : "#dde3ec"
                        }`,
                        borderRadius: "8px",
                        cursor: "not-allowed",
                        backgroundColor:
                          role === roleOption.value ? "#04497a" : "#ffffff",
                        color:
                          role === roleOption.value ? "#ffffff" : "#536387",
                        transition: "all 0.3s ease",
                        opacity: 0.6,
                      }}
                    >
                      <input
                        type="radio"
                        name="role"
                        value={roleOption.value}
                        checked={role === roleOption.value}
                        disabled={loggedUserRole === "employee" || loggedUserRole === "consultant"}
                        style={{ marginRight: "10px" }}
                      />
                      {roleOption.label}
                    </label>
                  ))}
                </div>
              </div>

              <div className="formbold-mb-3">
                <label className="formbold-form-label">Group</label>
                <select
                  className="formbold-form-input"
                  value={selectedGroup || ""}
                  disabled={loggedUserRole === "employee" || loggedUserRole === "consultant"}
                >
                  <option value="">{selectedGroup || "Not Assigned"}</option>
                  {groups.map((group) => (
                    <option key={group._id} value={group.groupname}>
                      {group.groupname}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div style={{ textAlign: "center" }}>
              <button
                type="button"
                onClick={() => setUploadConfirmationOpen(true)}
                className="formbold-btn"
              >
                Update Profile
              </button>
            </div>
          </form>
        </div>
      </div>

      <ConfirmationDialog
        open={uploadConfirmationOpen}
        handleClose={() => setUploadConfirmationOpen(false)}
        handleConfirm={handleSubmit}
        message="Are you sure you want to update your profile?"
      />

      <ToastContainer />
    </div>
  );
}

export default EditProfile;